"use client";

import React from "react";
import { Mail, Phone, Clock, MapPin, Loader, Send } from "lucide-react";
import { Link } from "@/components/common/Link";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
function ContactFormContent() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="16475" className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center bg-background rounded-3xl border border-border shadow-xl shadow-primary/5 animate-in fade-in zoom-in duration-300">
        <div className="w-16 h-16 mb-6 rounded-full bg-primary/10 flex items-center justify-center">
          <Send className="w-8 h-8 text-primary" />
        </div>
        <h3 className="text-2xl font-bold text-foreground mb-2">Quote Request Sent</h3>
        <p className="text-muted-foreground mb-8 max-w-xs mx-auto">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="min-w-[140px] rounded-full border-primary/20 hover:bg-primary/5">
          Send Another
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <FormField control={control} name="name" rules={{
        required: "Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Your Name</FormLabel>
              <FormControl>
                <Input placeholder="John Doe" className="bg-background focus-visible:ring-primary rounded-xl" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@example.com" className="bg-background focus-visible:ring-primary rounded-xl" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="subject" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Service Type</FormLabel>
            <FormControl>
              <Input placeholder="e.g. Residential Solar Cleaning" className="bg-background focus-visible:ring-primary rounded-xl" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground font-medium">Details</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your solar system (number of panels, roof type, etc.)..." className="min-h-[150px] bg-background focus-visible:ring-primary resize-none rounded-xl" {...field} />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-xl">
          <p className="text-sm text-destructive font-medium">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full bg-primary hover:bg-primary/90 text-primary-foreground h-12 text-base font-bold rounded-full shadow-lg shadow-primary/20 transition-all hover:scale-[1.02]">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Sending Request...
          </> : "Get My Free Quote"}
      </Button>
    </Form>;
}
export default function Contact() {
  const contactDetails = [{
    icon: Mail,
    label: "Email Us",
    value: "sales@powershine.com.au",
    href: "mailto:info@powershine.com.au"
  }, {
    icon: Phone,
    label: "Call Us",
    value: "0466 880 255",
    href: "tel:+61466880255"
  }, {
    icon: MapPin,
    label: "Service Area",
    value: "Sydney & Surrounding Suburbs",
    href: null
  }];
  return <section id="contact-us" className="py-20 bg-secondary/30 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -left-[10%] w-[50%] h-[50%] rounded-full bg-primary/10 blur-3xl animate-pulse" />
        <div className="absolute top-[40%] -right-[10%] w-[40%] h-[40%] rounded-full bg-secondary/40 blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {}
          <div className="space-y-10 pt-4">
            <div className="space-y-4">
              <h2 className="text-3xl md:text-5xl font-bold tracking-tight text-foreground">
                Get Your <span className="text-primary">Free Quote</span>
              </h2>
              <p className="text-lg text-muted-foreground max-w-md leading-relaxed">
                Our professional team is ready to help you maximize your solar efficiency and save on energy bills with expert cleaning services.
              </p>
            </div>

            <div className="grid gap-8">
              {contactDetails.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-4 group">
                  <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-background border border-border shadow-sm flex items-center justify-center group-hover:border-primary/50 group-hover:shadow-md transition-all duration-300">
                    <item.icon className="w-6 h-6 text-primary" />
                  </div>
                  <div className="space-y-1">
                    <h3 className="font-bold text-foreground">
                      {item.label}
                    </h3>
                    {item.href ? <Link to={item.href} className="text-muted-foreground hover:text-primary font-medium transition-colors block">
                        {item.value}
                      </Link> : <p className="text-muted-foreground font-medium">{item.value}</p>}
                  </div>
                </div>)}
            </div>

            <div className="p-6 bg-background/80 backdrop-blur-md rounded-3xl border border-border shadow-xl shadow-primary/5">
              <h4 className="font-bold text-foreground mb-2 flex items-center gap-2">
                <span className="w-2 h-2 rounded-full bg-primary" />
                Our Service Guarantee
              </h4>
              <p className="text-sm text-muted-foreground leading-relaxed">We use safe, specialised cleaning methods to ensure your solar panels are restored to peak performance without damage. Regular cleaning can improve energy output by up to 30%.</p>
            </div>
          </div>

          {}
          <div className="bg-background/40 backdrop-blur-xl rounded-[32px] border border-white/60 shadow-2xl shadow-primary/10 p-6 md:p-8 lg:p-10">
            <div className="mb-8">
              <h3 className="text-2xl font-bold text-foreground">Request a Quote</h3>
              <p className="text-muted-foreground mt-2">
                Fill out the form below and our solar specialists will get back to you within 24 hours.
              </p>
            </div>

            <FormProvider formKey="contact_form_services" formId="contact_form_services" sectionName="contact-section" defaultValues={{
            name: "",
            email: "",
            subject: "",
            message: ""
          }} validationRules={{
            name: {
              required: true
            },
            email: {
              required: true,
              pattern: "email"
            },
            message: {
              required: true
            }
          }} submitText="Get My Free Quote" successMessage="Thank you for contacting Power Shine Solar Cleaning. We have received your inquiry and will respond with a custom quote shortly." errorMessage="We encountered an error sending your message. Please try again or call us directly at 0466 880 255.">
              <ContactFormContent />
            </FormProvider>
          </div>
        </div>
      </div>
    </section>;
}